/**
 * Conteúdo rico — o vocabulário fechado, o saneador e os dois conversores.
 *
 * A RN-39 troca a fonte da verdade do caderno de Markdown para HTML. Este
 * módulo é o que torna isso seguro e reversível, e ele existe **antes** do
 * editor de propósito: as regras que definem o que pode ser guardado não
 * dependem de qual biblioteca vai produzir o conteúdo.
 *
 * Três peças:
 *
 *   `lerHtml`        HTML  → árvore tipada, por lista de permissão (RN-41)
 *   `sanearHtml`     HTML  → HTML, passando pela árvore. É o que grava.
 *   `markdownDeHtml` HTML  → Markdown, para a exportação (RN-35)
 *   `htmlDeMarkdown` Markdown → HTML, para a migração do que já existe
 *
 * **Não existe `innerHTML` em lugar nenhum do caminho.** Quem renderiza monta
 * elementos a partir da árvore, como já faz o leitor de Markdown — e é isso, e
 * não a lista de permissão, que fecha o caminho de injeção. A lista de
 * permissão existe para outra coisa: garantir o invariante 17.
 *
 * **Atributo `style` nunca sobrevive**, em nenhuma tag, em nenhuma
 * circunstância. A CSP `style-src 'self'` sem `style-src-attr` bloquearia o
 * estilo inline no navegador, e a falha apareceria no conteúdo do usuário — um
 * parágrafo sem cor, sem erro nenhum no console. Barrar na gravação e na
 * leitura é o que impede o conteúdo de chegar nesse estado.
 */
import { lerMarkdown } from "./markdown.js";
/* ------------------------------------------------------------ vocabulário */
/**
 * O vocabulário fechado da RN-40. Formatação é **classe**, e a lista de classes
 * é esta — não um seletor livre de valor.
 *
 * Os nomes não dizem a cor, dizem a posição na paleta. `rico-cor-2` continua
 * válido quando o tema troca de tinta; `rico-cor-vermelho` viraria mentira no
 * dia em que o vermelho da paleta mudar de matiz, e uma nota de dois anos atrás
 * ficaria descrevendo uma cor que não existe mais.
 *
 * Quatro de cada, e não uma escala aberta: paleta que aceita qualquer valor não
 * é paleta, e o par de cor e realce precisa passar contraste de texto normal
 * nos dois temas — o que só é verificável se o conjunto for finito.
 */
export const CLASSES_DE_COR = ["rico-cor-1", "rico-cor-2", "rico-cor-3", "rico-cor-4"];
export const CLASSES_DE_REALCE = ["rico-realce-1", "rico-realce-2", "rico-realce-3", "rico-realce-4"];
/**
 * Tamanho de trecho. `rico-p` e `rico-g` são os dois degraus antigos e ficam
 * **para sempre**: notas gravadas antes desta rodada os carregam, e tirá-los do
 * vocabulário faria o saneador apagar a formatação delas na primeira leitura.
 * Os seis numerados são a escala que a barra de ferramentas oferece agora.
 */
export const CLASSES_DE_TAMANHO = [
    "rico-p",
    "rico-g",
    "rico-tam-12",
    "rico-tam-14",
    "rico-tam-16",
    "rico-tam-18",
    "rico-tam-20",
    "rico-tam-24",
];
/** Os pontos da escala, na ordem do menu, em px. O padrão é a ausência deles. */
export const TAMANHOS_DO_TEXTO = [12, 14, 16, 18, 20, 24];
/**
 * Família de trecho. A ausência de classe é a fonte padrão da interface — um
 * vocabulário que nomeia o padrão obriga todo trecho a dizer "nada de especial".
 */
export const CLASSES_DE_FONTE = ["rico-fonte-serifa", "rico-fonte-mono"];
/** Alinhamento de bloco. Esquerda é o padrão e não tem classe. */
export const CLASSES_DE_ALINHAMENTO = ["rico-centro", "rico-direita", "rico-justificado"];
/** Recuo de parágrafo fora de lista. Três degraus; o zero é a ausência. */
export const CLASSES_DE_RECUO = ["rico-recuo-1", "rico-recuo-2", "rico-recuo-3"];
/**
 * Lista de tarefas, em classes.
 *
 * O `<input type="checkbox">` das extensões prontas não passa: `input` não está
 * na lista de tags permitidas, e `checked` não está na de atributos. Manter a
 * caixa como elemento exigiria abrir as duas listas para um elemento
 * interativo dentro de conteúdo do usuário, que é precisamente o que a RN-41
 * fecha. O estado vira classe no `<li>`, e a caixa é desenhada pela folha.
 */
export const CLASSES_DE_TAREFA = ["rico-tarefas", "rico-tarefa", "rico-feita"];
export const VOCABULARIO = [
    ...CLASSES_DE_COR,
    ...CLASSES_DE_REALCE,
    ...CLASSES_DE_TAMANHO,
    ...CLASSES_DE_FONTE,
    ...CLASSES_DE_ALINHAMENTO,
    ...CLASSES_DE_RECUO,
    ...CLASSES_DE_TAREFA,
];
const VOCABULARIO_SET = new Set(VOCABULARIO);
/**
 * As tags permitidas, e onde cada uma pode aparecer.
 *
 * `bloco` fecha o parágrafo anterior; `linha` só existe dentro de um bloco.
 * Tag fora desta tabela **não é escapada, é dissolvida**: o conteúdo dela
 * continua, a marcação some. Escapar deixaria `<script>` visível como texto no
 * caderno do usuário, que é ruído; dissolver preserva o que ele escreveu.
 */
const BLOCOS = new Set([
    "p", "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "pre", "hr",
    // A tabela entrou com a barra de ferramentas da Fase 2. Ela é bloco em todas
    // as partes: `tr` fecha o parágrafo anterior tanto quanto `li` fecha.
    "table", "thead", "tbody", "tr", "th", "td",
]);
const LINHAS = new Set(["strong", "em", "u", "s", "code", "a", "span", "mark", "br"]);
const VAZIAS = new Set(["br", "hr"]);
/**
 * A mesma lista, aberta para quem precisa percorrê-la.
 *
 * Quem precisa é o portão da forma (`caderno/estilo-dos-comandos.portao.test.ts`):
 * ele cobra estilo próprio de **toda** tag que sobrevive ao saneamento, e uma
 * lista reescrita lá não pegaria a tag acrescentada aqui. As duas guardas
 * continuam separadas — `ConteudoRico` tem a sua, de propósito.
 */
export const TAGS_PERMITIDAS = [...BLOCOS, ...LINHAS];
/* ---------------------------------------------------------------- leitura */
/** Só `http` e `https` — o mesmo critério do leitor de Markdown. */
function hrefSeguro(url) {
    const limpo = url.trim();
    return /^https?:\/\//i.test(limpo) ? limpo : null;
}
const ENTIDADES = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    "#39": "'",
    apos: "'",
    nbsp: " ",
};
function decodificar(texto) {
    return texto.replace(/&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z]+);/g, (inteiro, nome) => {
        const conhecida = ENTIDADES[nome];
        if (conhecida !== undefined)
            return conhecida;
        if (nome.startsWith("#x") || nome.startsWith("#X")) {
            const n = Number.parseInt(nome.slice(2), 16);
            return Number.isFinite(n) ? String.fromCodePoint(n) : inteiro;
        }
        if (nome.startsWith("#")) {
            const n = Number.parseInt(nome.slice(1), 10);
            return Number.isFinite(n) ? String.fromCodePoint(n) : inteiro;
        }
        return inteiro;
    });
}
export function escapar(texto) {
    return texto.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escaparAtributo(texto) {
    return escapar(texto).replace(/"/g, "&quot;");
}
/**
 * Lê os atributos de uma tag de abertura.
 *
 * Só dois sobrevivem: `class`, filtrada pelo vocabulário, e `href`, filtrada
 * pelo esquema. Todo o resto — `style` à frente — é descartado sem exceção e
 * sem configuração: um atributo que se pode ligar por engano não é barreira.
 */
function lerAtributos(bruto) {
    const classes = [];
    let href;
    const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
    let m;
    while ((m = re.exec(bruto)) !== null) {
        const nome = m[1].toLowerCase();
        const valor = decodificar(m[2] ?? m[3] ?? m[4] ?? "");
        if (nome === "class") {
            for (const c of valor.split(/\s+/)) {
                if (VOCABULARIO_SET.has(c) && !classes.includes(c))
                    classes.push(c);
            }
        }
        else if (nome === "href") {
            const seguro = hrefSeguro(valor);
            if (seguro)
                href = seguro;
        }
    }
    return href === undefined ? { classes } : { classes, href };
}
/**
 * HTML → árvore, por lista de permissão (RN-41).
 *
 * O leitor é tolerante de propósito: tag que não fecha, tag que fecha sem
 * abrir, tag desconhecida. Conteúdo de caderno não é documento validado, e um
 * parser que joga exceção transformaria uma nota levemente torta em nota
 * inacessível.
 *
 * `<script>` e `<style>` são o caso especial: eles são descartados **com o
 * conteúdo**. Dissolver a tag deixaria o corpo do script virar texto visível na
 * nota, que é a única coisa pior do que perdê-lo.
 */
export function lerHtml(html) {
    const raiz = [];
    const pilha = [];
    const topo = () => (pilha.length > 0 ? pilha[pilha.length - 1].filhos : raiz);
    const re = /<\/?([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;
    let posicao = 0;
    let m;
    function texto(bruto) {
        if (bruto === "")
            return;
        const valor = decodificar(bruto);
        if (valor === "")
            return;
        topo().push({ tipo: "texto", valor });
    }
    while ((m = re.exec(html)) !== null) {
        texto(html.slice(posicao, m.index));
        posicao = re.lastIndex;
        const tag = m[1].toLowerCase();
        const fechando = m[0].startsWith("</");
        if (tag === "script" || tag === "style") {
            // Pula até o fechamento correspondente, conteúdo incluído.
            if (!fechando) {
                const fim = html.toLowerCase().indexOf(`</${tag}>`, posicao);
                posicao = fim === -1 ? html.length : fim + tag.length + 3;
                re.lastIndex = posicao;
            }
            continue;
        }
        const permitida = BLOCOS.has(tag) || LINHAS.has(tag);
        if (fechando) {
            if (!permitida)
                continue;
            // Fecha o mais recente com este nome. Sem o índice, uma tag que fecha sem
            // ter aberto derrubaria a pilha inteira.
            const i = pilha.map((n) => n.tag).lastIndexOf(tag);
            if (i !== -1)
                pilha.length = i;
            continue;
        }
        if (!permitida)
            continue;
        const { classes, href } = lerAtributos(m[2] ?? "");
        const no = href === undefined
            ? { tipo: "elemento", tag, classes, filhos: [] }
            : { tipo: "elemento", tag, classes, href, filhos: [] };
        topo().push(no);
        if (!VAZIAS.has(tag))
            pilha.push({ tag, filhos: no.filhos });
    }
    texto(html.slice(posicao));
    return raiz;
}
/* ---------------------------------------------------------------- escrita */
function escreverNos(nos) {
    return nos.map(escreverNo).join("");
}
function escreverNo(no) {
    if (no.tipo === "texto")
        return escapar(no.valor);
    const atributos = [];
    if (no.classes.length > 0)
        atributos.push(` class="${escaparAtributo(no.classes.join(" "))}"`);
    if (no.href !== undefined)
        atributos.push(` href="${escaparAtributo(no.href)}"`);
    const abertura = `<${no.tag}${atributos.join("")}>`;
    if (VAZIAS.has(no.tag))
        return abertura;
    return `${abertura}${escreverNos(no.filhos)}</${no.tag}>`;
}
/**
 * O que grava (RN-41). Ler e reescrever é o saneamento inteiro: o que não
 * sobrevive à árvore não sobrevive à gravação.
 *
 * Rodar de novo sobre a própria saída não muda nada — é o que permite sanear na
 * gravação **e** na renderização sem o conteúdo ir se degradando a cada passo.
 */
export function sanearHtml(html) {
    return escreverNos(lerHtml(html));
}
/** O texto sem marcação nenhuma, para busca e resumo. */
export function textoPuroDeHtml(html) {
    const partes = [];
    const visitar = (nos) => {
        for (const no of nos) {
            if (no.tipo === "texto")
                partes.push(no.valor);
            else {
                if (BLOCOS.has(no.tag) || no.tag === "br")
                    partes.push(" ");
                visitar(no.filhos);
            }
        }
    };
    visitar(lerHtml(html));
    return partes.join("").replace(/\s+/g, " ").trim();
}
/* ------------------------------------------------- HTML para Markdown ---- */
/**
 * HTML → Markdown, para a exportação (RN-35).
 *
 * **A perda é o ponto, e ela é previsível.** Markdown não tem cor de texto,
 * realce, tamanho nem alinhamento: essas quatro coisas somem, e somem em
 * silêncio no arquivo — por isso a RN-39 exige que a tela avise **antes** de
 * gerar, dizendo quantas notas perdem formatação. `temFormatacaoDecorativa`
 * abaixo é o que responde essa pergunta.
 *
 * O que sobrevive é a estrutura: título, lista, citação, código, ênfase, força
 * e link. É o mesmo conjunto que o leitor de Markdown reconhece, o que torna a
 * ida e a volta estáveis para tudo que não é decoração.
 */
export function markdownDeHtml(html) {
    const blocos = [];
    function linha(nos) {
        return nos
            .map((no) => {
            if (no.tipo === "texto")
                return no.valor;
            const dentro = linha(no.filhos);
            switch (no.tag) {
                case "strong":
                    return dentro === "" ? "" : `**${dentro}**`;
                case "em":
                    return dentro === "" ? "" : `*${dentro}*`;
                case "code":
                    return dentro === "" ? "" : `\`${dentro}\``;
                case "a":
                    return no.href ? `[${dentro}](${no.href})` : dentro;
                case "br":
                    return "\n";
                // `u`, `s`, `span` e `mark` são decoração: o texto fica, a marcação
                // some. É exatamente a perda que a tela avisa antes de exportar.
                default:
                    return dentro;
            }
        })
            .join("");
    }
    function bloco(nos, prefixo = "") {
        for (const no of nos) {
            if (no.tipo === "texto") {
                const solto = no.valor.trim();
                if (solto !== "")
                    blocos.push(prefixo + solto);
                continue;
            }
            switch (no.tag) {
                case "h1":
                case "h2":
                case "h3": {
                    const n = Number(no.tag.slice(1));
                    blocos.push(`${"#".repeat(n)} ${linha(no.filhos).trim()}`);
                    break;
                }
                case "p": {
                    const t = linha(no.filhos).trim();
                    if (t !== "")
                        blocos.push(prefixo + t);
                    break;
                }
                case "blockquote": {
                    const antes = blocos.length;
                    bloco(no.filhos);
                    for (let i = antes; i < blocos.length; i++)
                        blocos[i] = `> ${blocos[i]}`;
                    break;
                }
                case "pre": {
                    blocos.push("```\n" + textoDe(no.filhos).replace(/\n+$/, "") + "\n```");
                    break;
                }
                case "hr":
                    blocos.push("---");
                    break;
                case "ul":
                case "ol": {
                    const itens = no.filhos.filter((f) => f.tipo === "elemento" && f.tag === "li");
                    const linhas = itens.map((f, i) => no.tag === "ol" ? `${i + 1}. ${linha(f.filhos).trim()}` : `- ${linha(f.filhos).trim()}`);
                    if (linhas.length > 0)
                        blocos.push(linhas.join("\n"));
                    break;
                }
                default: {
                    // Um bloco desconhecido ou um trecho de linha solto na raiz: trata
                    // como parágrafo, em vez de sumir com o texto.
                    const t = linha([no]).trim();
                    if (t !== "")
                        blocos.push(prefixo + t);
                }
            }
        }
    }
    bloco(lerHtml(html));
    return blocos.join("\n\n").trim();
}
function textoDe(nos) {
    return nos.map((n) => (n.tipo === "texto" ? n.valor : textoDe(n.filhos))).join("");
}
/**
 * Se a nota tem formatação que a exportação perde (RN-39).
 *
 * É a pergunta que a tela de exportação faz por nota antes de gerar o arquivo.
 * Cor, realce, tamanho e alinhamento são classe; sublinhado e tachado são tag.
 * Nenhum dos seis tem equivalente em Markdown.
 */
export function temFormatacaoDecorativa(html) {
    let achou = false;
    const visitar = (nos) => {
        for (const no of nos) {
            if (achou || no.tipo === "texto")
                continue;
            if (no.classes.length > 0 || no.tag === "u" || no.tag === "s" || no.tag === "mark")
                achou = true;
            else
                visitar(no.filhos);
        }
    };
    visitar(lerHtml(html));
    return achou;
}
/* ------------------------------------------------- Markdown para HTML ---- */
/**
 * Markdown → HTML, para a migração do que já está guardado.
 *
 * Ela existe uma vez só, mas precisa existir de verdade: a RN-39 troca a fonte
 * da verdade, e converter na leitura para sempre seria manter as duas fontes ao
 * mesmo tempo — que é justamente o que a ADR 012 recusa.
 *
 * A conversão é total no sentido que importa: o Markdown que o leitor
 * reconhece é um subconjunto do que o HTML permite, então nada se perde nesta
 * direção. A perda mora na volta, e é a que a exportação avisa.
 */
export function htmlDeMarkdown(markdown) {
    const trecho = (t) => {
        switch (t.tipo) {
            case "texto":
                return escapar(t.valor);
            case "forte":
                return `<strong>${escapar(t.valor)}</strong>`;
            case "enfase":
                return `<em>${escapar(t.valor)}</em>`;
            case "codigo":
                return `<code>${escapar(t.valor)}</code>`;
            case "link":
                return `<a href="${escaparAtributo(t.href)}">${escapar(t.texto)}</a>`;
        }
    };
    const trechos = (ts) => ts.map(trecho).join("");
    return lerMarkdown(markdown)
        .map((b) => {
        switch (b.tipo) {
            case "titulo":
                return `<h${b.nivel}>${trechos(b.trechos)}</h${b.nivel}>`;
            case "paragrafo":
                return `<p>${trechos(b.trechos)}</p>`;
            case "citacao":
                return `<blockquote><p>${trechos(b.trechos)}</p></blockquote>`;
            case "codigo":
                return `<pre><code>${escapar(b.texto)}</code></pre>`;
            case "regua":
                return "<hr>";
            case "lista": {
                const tag = b.ordenada ? "ol" : "ul";
                return `<${tag}>${b.itens.map((i) => `<li>${trechos(i)}</li>`).join("")}</${tag}>`;
            }
        }
    })
        .join("");
}
/** Um começo de texto para a lista de notas, cortado em palavra inteira. */
export function resumoDeHtml(html, limite = 160) {
    const puro = textoPuroDeHtml(html);
    if (puro.length <= limite)
        return puro;
    const corte = puro.slice(0, limite);
    const ultimoEspaco = corte.lastIndexOf(" ");
    return `${(ultimoEspaco > limite * 0.6 ? corte.slice(0, ultimoEspaco) : corte).trimEnd()}…`;
}
//# sourceMappingURL=conteudo.js.map